import React from 'react';
import { useSelector } from 'react-redux';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import VodButton from '../../../../shared/VodButton/VodButton';
import { getResponsiveImageSources } from '../../../../shared/OptimizedImage/optimizedImage';

import {
  getContentLatestFromProgram,
  normalizeProgramEpisodes,
  type ProgramLatestEpisodeItem,
} from '../../../../../services/api/contentLatestFromProgram';
import { programEpisodesDataKey, selectViewData } from '../../viewSsrData';
import { resolveEpisodeDate } from './programEpisodeHelpers';

type Props = {
  programPath?: string;
  parentTitle?: string;
  parentImage?: string;
};

function getEpisodePlaceholderLabel(title?: string) {
  if (!title) return 'Áudio';

  const words = title.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  const initials = words.map((word) => word[0]?.toUpperCase()).join('');

  return initials || 'Áudio';
}

function parseDateAiring(value?: string) {
  if (!value) {
    return { date: '', time: '' };
  }

  const [date = '', time = ''] = value.split('|').map((part) => part.trim());

  return { date, time };
}

export default function ProgramEpisodes({
  programPath = '',
  parentTitle = '',
  parentImage = '',
}: Props) {
  const preloadedData = useSelector((state: any) =>
    selectViewData(state, programEpisodesDataKey(programPath)),
  );
  const hasPreloadedData = Array.isArray(preloadedData);
  const [items, setItems] = React.useState<ProgramLatestEpisodeItem[]>(() =>
    normalizeProgramEpisodes(preloadedData, parentTitle),
  );
  const [page, setPage] = React.useState(1);
  const [loadingInitial, setLoadingInitial] = React.useState(!hasPreloadedData);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(true);
  const playerMode = useSelector((state: any) => state.player?.mode ?? '');
  const currentVodUrl = useSelector((state: any) => state.player?.vodUrl || '');
  const intentPlaying = useSelector(
    (state: any) => !!state.player?.intentPlaying,
  );

  React.useEffect(() => {
    if (hasPreloadedData) {
      const nextItems = normalizeProgramEpisodes(preloadedData, parentTitle);
      setItems(nextItems);
      setPage(1);
      setHasMore(nextItems.length > 0);
      setLoadingInitial(false);
      return;
    }

    let cancelled = false;

    async function loadInitial() {
      if (!programPath) {
        setItems([]);
        setHasMore(false);
        setLoadingInitial(false);
        return;
      }

      setLoadingInitial(true);

      try {
        const result = await getContentLatestFromProgram({
          programPath,
          page: 1,
          parentTitle,
        });

        if (cancelled) return;

        setItems(result.items);
        setPage(1);
        setHasMore(result.items.length > 0);
      } catch (error) {
        if (cancelled) return;

        setItems([]);
        setPage(1);
        setHasMore(false);
      } finally {
        if (!cancelled) {
          setLoadingInitial(false);
        }
      }
    }

    loadInitial();

    return () => {
      cancelled = true;
    };
  }, [hasPreloadedData, parentTitle, preloadedData, programPath]);

  const handleLoadMore = React.useCallback(async () => {
    if (!programPath || loadingMore || !hasMore) return;

    const nextPage = page + 1;
    setLoadingMore(true);

    try {
      const result = await getContentLatestFromProgram({
        programPath,
        page: nextPage,
        parentTitle,
      });

      if (!result.items.length) {
        setHasMore(false);
        return;
      }

      setItems((prev) => {
        const existingIds = new Set(prev.map((item) => item['@id']));
        const uniqueNext = result.items.filter(
          (item) => !existingIds.has(item['@id']),
        );

        return [...prev, ...uniqueNext];
      });

      setPage(nextPage);
      setHasMore(true);
    } catch (error) {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [programPath, loadingMore, hasMore, page, parentTitle]);

  if (loadingInitial) {
    return (
      <section className="program-view__episodes" aria-busy="true">
        <div className="program-view__episodes-top">
          <div className="program-view__sectionHeader">
            <h2 className="program-view__sectionTitle">Últimos episódios</h2>
          </div>

          <p className="program-view__episodesState">Carregando episódios...</p>
        </div>
      </section>
    );
  }

  if (!items.length) {
    return null;
  }

  return (
    <section className="program-view__episodes">
      <div className="program-view__episodes-top">
        <div className="program-view__sectionHeader">
          <h2 className="program-view__sectionTitle">Últimos episódios</h2>
        </div>

        <div className="program-view__episodesList">
          {items.map((item) => {
            const meta = parseDateAiring(resolveEpisodeDate(item));
            const pageUrl = item['@id'] || '';
            const title = item.title || '';
            const hasMediaLink = !!pageUrl;
            const audioUrl = item.audio || '';
            const image = item.image || parentImage;
            const imageSources = getResponsiveImageSources(
              image,
              undefined,
              'thumb',
            );

            const isPlayingThis =
              !!audioUrl &&
              playerMode === 'vod' &&
              currentVodUrl === audioUrl &&
              intentPlaying;

            return (
              <article
                key={item['@id']}
                className={
                  isPlayingThis
                    ? 'program-view__episodeCard program-view__episodeCard--playing'
                    : 'program-view__episodeCard'
                }
              >
                <div className="program-view__episodeMedia">
                  {hasMediaLink ? (
                    <UniversalLink
                      href={pageUrl}
                      className="program-view__episodeMediaLink"
                      aria-label={`Abrir episódio: ${title}`}
                      title={title}
                    >
                      {imageSources ? (
                        <img
                          className="program-view__episodeImage"
                          src={imageSources.src}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          width={84}
                          height={56}
                        />
                      ) : (
                        <div
                          className="program-view__episodePlaceholder"
                          aria-hidden="true"
                        >
                          <span className="program-view__episodePlaceholderText">
                            {getEpisodePlaceholderLabel(title)}
                          </span>
                        </div>
                      )}
                    </UniversalLink>
                  ) : imageSources ? (
                    <img
                      className="program-view__episodeImage"
                      src={imageSources.src}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      width={84}
                      height={56}
                    />
                  ) : (
                    <div
                      className="program-view__episodePlaceholder"
                      aria-hidden="true"
                    >
                      <span className="program-view__episodePlaceholderText">
                        {getEpisodePlaceholderLabel(title)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="program-view__episodeContent">
                  {hasMediaLink ? (
                    <UniversalLink
                      href={pageUrl}
                      className="program-view__episodeTitleLink"
                      title={title}
                    >
                      <h3 className="program-view__episodeTitle">{title}</h3>
                    </UniversalLink>
                  ) : (
                    <h3 className="program-view__episodeTitle">{title}</h3>
                  )}

                  {(meta.date || meta.time) && (
                    <div className="program-view__episodeMeta">
                      {meta.date ? (
                        <span className="program-view__episodeDate">
                          {meta.date}
                        </span>
                      ) : null}

                      {meta.date && meta.time ? (
                        <span
                          className="program-view__episodeMetaSep"
                          aria-hidden="true"
                        >
                          |
                        </span>
                      ) : null}

                      {meta.time ? (
                        <span className="program-view__episodeTime">
                          {meta.time}
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>

                <div className="program-view__episodeAction">
                  {isPlayingThis ? (
                    <div className="program-view__nowPlaying">
                      <span
                        className="program-view__equalizer"
                        aria-hidden="true"
                      >
                        <i />
                        <i />
                        <i />
                      </span>
                    </div>
                  ) : null}
                  <VodButton
                    url={item.audio}
                    pageUrl={pageUrl}
                    title={title}
                    subtitle={item.parentTitle}
                    image={image}
                    label="Ouça agora"
                    fallbackLabel="Saiba mais"
                    origin="ProgramViewEpisodes"
                    className={
                      isPlayingThis
                        ? 'program-view__episodeCta pressable vodButton--active'
                        : 'program-view__episodeCta pressable'
                    }
                    disabled={!item.audio && !pageUrl}
                  />
                </div>
              </article>
            );
          })}
        </div>
      </div>

      {hasMore ? (
        <button
          type="button"
          className="program-view__episodesMore"
          onClick={handleLoadMore}
          disabled={loadingMore}
          aria-label={
            loadingMore
              ? 'Carregando mais episódios'
              : 'Carregar mais episódios'
          }
        >
          {loadingMore ? 'Carregando...' : 'Ver mais'}
        </button>
      ) : null}
    </section>
  );
}
